// id.<root> authenticated state — the account hub. Identity summary, depots
// (list + link via the B2C HUB_LINKDEPOT journey), 2FA device management, and
// sign out. Mirrors id-online's Dashboard, scoped to the identity surface.
//
// Depots load from the adapter's session-authed /auth/me/depots, with the
// JWT-embedded list as the instant fast-path (same approach as id-online).

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { authClient } from "../lib/auth";
import {
  brandLabel,
  resolveBrand,
  resolveTenant,
  surfaceUrl,
  TENANTS,
} from "../lib/brand";
import type { SessionView } from "../lib/session";

interface DepotItem {
  depotKey: string;
  primary?: boolean;
}

const MAX_POLLS = 16; // ~16 × 2.5s ≈ 40s

async function fetchDepots(): Promise<DepotItem[]> {
  const res = await fetch(`/api/mf-auth-adapter/auth/me/depots`, {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`depots_load_failed_${res.status}`);
  return (await res.json()) as DepotItem[];
}

export interface AccountProps {
  session: SessionView;
}

export function Account({ session }: AccountProps) {
  const brand = resolveBrand();
  const tenant = resolveTenant();
  const label = brandLabel(tenant);
  const identity = session.email ?? session.sub;
  const initial = (identity?.trim()[0] ?? "?").toUpperCase();
  const accent = TENANTS[tenant].brand.paletteAccent;

  // "Link a depot" → full-page B2C HUB_LINKDEPOT (CustomerID + PIN); the adapter
  // links the proven depot onto THIS session and 302s back here.
  const linkDepot = () =>
    authClient.loginRedirect({
      brand,
      flow: "linkdepot",
      return: window.location.origin + "/",
    });

  let polls = 0;
  const depots = useQuery({
    queryKey: ["depots", session.sub],
    queryFn: async () => {
      polls += 1;
      return fetchDepots();
    },
    initialData: (session.depots ?? []).map(
      (depotKey): DepotItem => ({
        depotKey,
        primary: depotKey === session.primaryDepot,
      }),
    ),
    retry: false,
    refetchInterval: (query) => {
      const data = query.state.data as DepotItem[] | undefined;
      if (data && data.length > 0) return false;
      if (polls >= MAX_POLLS) return false;
      return 2500;
    },
  });

  return (
    <>
      <div className="hub-identity">
        <span className="hub-avatar" aria-hidden="true" style={{ background: accent }}>
          {initial}
        </span>
        <span className="hub-identity-meta">
          <span className="hub-identity-label">Signed in as</span>
          <span className="hub-identity-value">{identity ?? "(unknown)"}</span>
        </span>
      </div>
      <header className="hub-page-header">
        <h1>Your {label} account</h1>
        <p>Manage your depots and security from one place.</p>
      </header>

      <section className="hub-explainer" data-testid="depots-section">
        <h2>Depots</h2>
        {depots.data.length === 0 ? (
          <p style={{ margin: 0 }} data-id-depots-empty>
            {depots.isFetching ? "Loading your depots…" : "No depots on file."}
          </p>
        ) : (
          <ul className="hub-qa-list" data-id-depots-list>
            {depots.data.map((d) => (
              <li key={d.depotKey}>
                <code>{d.depotKey}</code>
                {d.primary ? <strong> (primary)</strong> : null}
              </li>
            ))}
          </ul>
        )}
        <p className="hub-cta-row" style={{ margin: "14px 0 0", padding: 0, textAlign: "left" }}>
          <button type="button" className="hub-cta-button" data-id-linkdepot onClick={linkDepot}>
            Link a depot (Customer ID + PIN)
          </button>
        </p>
      </section>

      <section className="hub-explainer">
        <h2>Security</h2>
        <p className="hub-cta-row" style={{ margin: 0, padding: 0, textAlign: "left" }}>
          <Link
            className="hub-cta-button secondary"
            to="/devices"
            data-id-devices
            style={{ textDecoration: "none" }}
          >
            Manage 2FA devices
          </Link>
        </p>
        <p className="hub-banner hub-banner-warn" style={{ marginTop: 14 }} data-id-password-note>
          Changing your password is handled by {label} support for now —
          self-service is coming once the core credential API is wired.
        </p>
      </section>

      <main className="hub-cta-row">
        <button
          type="button"
          className="hub-cta-button secondary"
          data-id-signout
          onClick={() => {
            authClient.logout().catch(() => {
              window.location.href = surfaceUrl("altersvorsorge", tenant) + "/";
            });
          }}
        >
          Sign out
        </button>
      </main>
    </>
  );
}
